/**
 * Phase 21.3a5 Batch 2 — 💡 Metric education tooltips.
 *
 * Single source of truth for every metric definition surfaced across
 * Dashboard, Boost Pulse, Artist Profile and share cards. Keeps the
 * copy accurate to the actual server formulas (see promo_plays.py and
 * platform_settings_routes.py `formulas` block).
 *
 * Usage:
 *   <MetricTooltip metric="reach">
 *     <span>Boosted Reach</span>
 *   </MetricTooltip>
 *
 * Or as a standalone icon next to any label:
 *   <MetricInfoIcon metric="reach" />
 */
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const METRIC_DEFINITIONS = {
  plays: {
    label: "Plays",
    text:
      "Real listens counted for this track. Never inflated. Used by admin, A&R and payouts.",
  },
  display_plays: {
    label: "Display plays",
    text:
      "The public number shown on featured cards, share cards and profile. = Real plays + promotional plays granted when your track was featured or spotlighted.",
  },
  promotional_plays: {
    label: "Promotional plays",
    text:
      "Bonus plays credited to your track when it was featured. Capped per tier, lifetime-capped per track, always audited. Does NOT touch your real play_count.",
  },
  reach: {
    label: "Reach",
    text:
      "Extra visibility injected by boost + floor when your track was featured. Reach = boost_applied + floor_applied.",
  },
  impressions: {
    label: "Impressions",
    text: "Modeled surface exposures = Reach × 8 (RGM's impression multiplier).",
  },
  boost_applied: {
    label: "Boost applied",
    text:
      "Reach units this feature added to your track. Applied over a 24–72h drip after being featured.",
  },
  floor_applied: {
    label: "Floor applied",
    text:
      "Minimum guaranteed reach for a featured track. Kicks in if the boost drip underperforms.",
  },
  momentum: {
    label: "Momentum",
    text:
      "Live signal from your last 24h: plays + gifts + reactions. Drives your card's momentum badge.",
  },
  engagement_score: {
    label: "Engagement score",
    text:
      "Ranking math on Top Artists = plays + (likes × 2) + (followers × 5).",
  },
  followers: {
    label: "Followers",
    text:
      "Real users following you. Excludes deleted / test / phantom IDs (Option A reverse-view).",
  },
  features: {
    label: "Features",
    text:
      "Times your tracks got featured (yours + collabs where you're tagged).",
  },
  spotlight_wins: {
    label: "Spotlight wins",
    text: "Weekly Spotlight 1st-place finishes.",
  },
  gift_coins: {
    label: "Gift coins",
    text:
      "Fan gifts your tracks received. Coins convert to earnings via the Creator Hub.",
  },
};

const InfoDot = ({ testId }) => (
  <span
    aria-hidden="true"
    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/10 text-[9px] font-black text-white/70 hover:bg-purple-500/30 hover:text-white transition-colors cursor-help select-none"
    data-testid={testId}
  >
    ?
  </span>
);

export const MetricTooltip = ({ metric, children, className = "" }) => {
  const def = METRIC_DEFINITIONS[metric];
  const label = def?.label || metric;
  const text = def?.text || "";
  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1.5 ${className}`}
            data-testid={`metric-tooltip-trigger-${metric}`}
          >
            {children}
            <InfoDot testId={`metric-tooltip-icon-${metric}`} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-[#13131a] border border-purple-500/40 text-white text-xs leading-relaxed"
          data-testid={`metric-tooltip-content-${metric}`}
        >
          <p className="font-black text-purple-200 mb-1">{label}</p>
          <p className="text-gray-200">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MetricInfoIcon = ({ metric, className = "" }) => {
  const def = METRIC_DEFINITIONS[metric];
  const label = def?.label || metric;
  const text = def?.text || "";
  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`What is ${label}?`}
            className={`inline-flex ${className}`}
            data-testid={`metric-info-icon-${metric}`}
          >
            <InfoDot />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-[#13131a] border border-purple-500/40 text-white text-xs leading-relaxed"
          data-testid={`metric-tooltip-content-${metric}`}
        >
          <p className="font-black text-purple-200 mb-1">{label}</p>
          <p className="text-gray-200">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default MetricTooltip;
